import React from 'react';
import { useSite } from '../useSite';
import { Seo } from '../components/Seo';
import { ButtonLink, CheckList, Container, SectionHeading } from '../components/Primitives';
import { CtaBand, PageHero, ServiceRows } from '../components/Sections';
import { SERVICE_KEYS } from '../routes';

export function AudiencePage({ audience }) {
  const { t } = useSite();
  const a = t.audiences[audience];
  const s = t.seo[audience];
  return (
    <>
      <Seo title={s.title} description={s.description} />
      <PageHero overline={a.overline} title={a.h1} intro={a.intro} photo={audience === 'investors' ? 'residence' : 'entrepreneur'} />
      <section className="bg-white py-20 md:py-24">
        <Container className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5"><h2 className="font-serif text-3xl text-kombu-green md:text-4xl">{a.challengesTitle}</h2></div>
          <div className="lg:col-span-7"><CheckList items={a.challenges} testId="audience-challenges" /></div>
        </Container>
      </section>
      <section className="bg-bone-light py-20 md:py-24">
        <Container className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5"><h2 className="font-serif text-3xl text-kombu-green md:text-4xl">{a.approachTitle}</h2></div>
          <div className="lg:col-span-7"><CheckList items={a.approach} testId="audience-approach" /></div>
        </Container>
      </section>
      <section className="bg-white py-20 md:py-24">
        <Container>
          <SectionHeading overline={t.common.relatedExpertise} title={t.home.expertise.title} />
          <div className="mt-10"><ServiceRows keys={a.services} /></div>
          <p className="mt-10 max-w-3xl text-xs leading-relaxed text-cafe-noir/60">{t.common.individualAnalysis}</p>
        </Container>
      </section>
      <CtaBand />
    </>
  );
}

export function ServicePage({ service }) {
  const { t, p } = useSite();
  const sv = t.services[service];
  const s = t.seo[service];
  const L = t.serviceLabels;
  return (
    <>
      <Seo title={s.title} description={s.description} />
      <PageHero overline={t.nav.expertise} title={sv.h1} intro={sv.intro} photo={{ strategic: 'meeting', international: 'skyline', realestate: 'residence', accounting: 'advisory-desk' }[service]}>
        {sv.forms && (
          <div className="mt-8" data-testid="service-forms">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-cafe-noir/60">{L.forms}</p>
            <ul className="flex flex-wrap gap-2">
              {sv.forms.map((f) => <li key={f} className="border border-moss-green/40 bg-white px-3 py-1.5 text-xs font-semibold tracking-wide text-kombu-green">{f}</li>)}
            </ul>
          </div>
        )}
      </PageHero>
      <section className="bg-white py-20 md:py-24">
        <Container className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h2 className="mb-8 font-serif text-3xl text-kombu-green md:text-4xl">{L.scope}</h2>
            <CheckList items={sv.scope} testId="service-scope" />
          </div>
          <aside className="lg:col-span-5">
            <div className="border border-tan/60 bg-bone-light p-8">
              <h2 className="font-serif text-2xl text-kombu-green">{L.whoFor}</h2>
              <ul className="mt-5 space-y-3 text-[15px] leading-relaxed text-cafe-noir/85" data-testid="service-who-for">
                {sv.whoFor.map((w) => <li key={w} className="border-b border-tan/40 pb-3 last:border-0">{w}</li>)}
              </ul>
            </div>
            <p className="mt-6 text-xs leading-relaxed text-cafe-noir/60" data-testid="service-disclaimer">{t.common.individualAnalysis}</p>
          </aside>
        </Container>
      </section>
      {service === 'accounting' && (
        <section className="bg-white pb-4" data-testid="service-plans-section">
          <Container><ButtonLink to={p('plans')} variant="secondary" testId="service-plans-link">{t.plansPage.teaser.cta}</ButtonLink></Container>
        </section>
      )}
      <section className="bg-bone-light py-16 md:py-20" data-testid="service-related">
        <Container>
          <h2 className="font-serif text-2xl text-kombu-green md:text-3xl">{t.common.relatedExpertise}</h2>
          <div className="mt-8"><ServiceRows keys={SERVICE_KEYS.filter((k) => k !== service)} /></div>
        </Container>
      </section>
      <CtaBand title={L.cta} text={L.ctaText} />
    </>
  );
}

export function ExpertisePage() {
  const { t } = useSite();
  const e = t.expertisePage;
  return (
    <>
      <Seo title={t.seo.expertise.title} description={t.seo.expertise.description} />
      <PageHero overline={e.overline} title={e.h1} intro={e.intro} />
      <section className="bg-white py-16 md:py-24"><Container><ServiceRows /></Container></section>
      <CtaBand />
    </>
  );
}
